import { type Action, type Thunk, action, thunk } from "easy-peasy";

import type { FirebaseAllAvailabilitySlot } from "./allAvailabilityTypes";
import { getDatesForRestaurantDiscountFetch } from "./getDatesForRestaurantDiscountFetch";
import subscribeDiscountSession, {
  type DiscountDateAvailabilityPayload,
  clearDiscountSessionSnapshotCache,
  getRememberedDiscountSessionDates,
  getSessionSubscribedDefinitions,
  latestDiscountSessionSnapshotByPath,
  rememberDiscountSessionDates,
  replayDiscountSessionSnapshots,
  sessionDefinitionToPath,
} from "./subscribeDiscountSession";
import addUnsub from "./subscriptionManager/addUnsub";
import getMissingDefinitions from "./subscriptionManager/getMissingDefinitions";
import getUnusedPaths from "./subscriptionManager/getUnusedPaths";
import multiSubscribe from "./subscriptionManager/multiSubscribe";
import type { UnsubsByPath } from "./subscriptionManager/types";
import unsubAllFromManager from "./subscriptionManager/unsubAll";
import unsubOneFromManager from "./subscriptionManager/unsubOne";
import type { DataSnapshot, Unsubscribe } from "../dependencies/firebase";
import { Logger } from "../dependencies/logger";
import type { ByRestaurantId } from "../model/types";
import type { NonEmptyArray } from "../types";
import {
  clearAvailabilitySnapshotCache,
  fingerprintFirebaseAllAvailabilityRaw,
  getCachedNormalizedAvailability,
  rememberNormalizedAvailability,
} from "../utils/discountAvailability";

export type { FirebaseAllAvailabilitySlot } from "./allAvailabilityTypes";

const logger = new Logger(
  "RESTAURANT_STORE - FIREBASE - ALL_AVAILABILITY",
  "LOG",
);
// logger.enable();

/** Week mode uses session-level paths; specific dates use per-date paths. */
export type FirebaseAvailabilityPathMode = "session" | "date";

export type DatePathDefinition = {
  restaurantId: number;
  session: SessionType;
  date: string;
  onlyOnce: boolean;
};

export const dateDefinitionToPath = ({
  restaurantId,
  session,
  date,
}: Omit<DatePathDefinition, "onlyOnce">) =>
  `variableDiscountAvailability/${restaurantId}/${session.toLowerCase()}/${date}/`;

export const getDateSubscribedDefinitions = (
  restaurantIds: number[],
  sessionsById: ByRestaurantId<SessionType[]>,
  datesById: ByRestaurantId<NonEmptyArray<string>>,
  onlyOnceById: ByRestaurantId<boolean>,
): DatePathDefinition[] => {
  const definitions: DatePathDefinition[] = [];

  for (const restaurantId of restaurantIds) {
    const onlyOnce = onlyOnceById[restaurantId] || false;
    const sessions = sessionsById[restaurantId] || [];
    const dates = getDatesForRestaurantDiscountFetch(restaurantId, datesById);

    for (const session of sessions) {
      for (const date of dates) {
        definitions.push({
          restaurantId,
          session,
          date,
          onlyOnce,
        });
      }
    }
  }

  return definitions;
};

function getAvailabilityFromDateChildSnapshot(
  snapShot: DataSnapshot | undefined,
  path: string,
): FirebaseAllAvailabilitySlot[] | null {
  const availability = snapShot?.val();
  const rawFingerprint = fingerprintFirebaseAllAvailabilityRaw(availability);

  // Fingerprint hit → same array ref (store merge no-ops). Still call onValue
  // so nested stores that share this process cache can merge if needed.
  const cached = getCachedNormalizedAvailability(path, rawFingerprint);
  if (cached !== undefined) {
    return cached;
  }

  if (!availability) {
    rememberNormalizedAvailability(path, rawFingerprint, null);
    return null;
  }

  // Duplicate rows are left for the derive to collapse, because which one survives
  // depends on the party size and that is not known here. Diner-availability fields
  // are not a safe key to collapse on either: they are often absent, and the key then
  // stops distinguishing the bands it exists to separate.
  const slots = Object.values(availability).filter(
    (availabilitySlot): availabilitySlot is FirebaseAllAvailabilitySlot =>
      !!availabilitySlot,
  );

  rememberNormalizedAvailability(path, rawFingerprint, slots);
  return slots;
}

const unsubPath = (unsubsByPath: UnsubsByPath, path: string) => {
  clearDiscountSessionSnapshotCache(path);
  unsubOneFromManager(unsubsByPath, path);
};

/**
 * Replays cached session snapshots for restaurants that are not currently live
 * subscribed (typically onlyOnce / off-screen). In-view live sync already
 * refreshes date windows for active listeners.
 */
export async function replayCachedSessionSnapshots(
  restaurantIds: number[],
  sessionsById: ByRestaurantId<SessionType[]>,
  datesById: ByRestaurantId<NonEmptyArray<string>>,
  unsubsByPath: UnsubsByPath,
  onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>,
): Promise<void> {
  const sessionDefs = getSessionSubscribedDefinitions(
    restaurantIds,
    sessionsById,
    Object.fromEntries(
      restaurantIds.map((restaurantId) => [restaurantId, true]),
    ),
  );
  const skipDefinitions = sessionDefs.filter((definition) => {
    const path = sessionDefinitionToPath(definition);
    return (
      unsubsByPath[path] != null ||
      !latestDiscountSessionSnapshotByPath.has(path)
    );
  });

  if (sessionDefs.length === skipDefinitions.length) {
    return;
  }

  await replayDiscountSessionSnapshots(
    sessionDefs,
    skipDefinitions,
    datesById,
    getAvailabilityFromDateChildSnapshot,
    onValue,
  );
}

async function subscribeSessionPaths(
  restaurantIds: number[],
  sessionsById: ByRestaurantId<SessionType[]>,
  datesById: ByRestaurantId<NonEmptyArray<string>>,
  onlyOnceById: ByRestaurantId<boolean>,
  unsubsByPath: UnsubsByPath,
  addUnsubAction: (path: string, unsub: Unsubscribe) => void,
  onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>,
  abortSignal: AbortSignal,
): Promise<void> {
  const sessionDefs = getSessionSubscribedDefinitions(
    restaurantIds,
    sessionsById,
    onlyOnceById,
  );
  for (const definition of sessionDefs) {
    if (!definition.onlyOnce) {
      rememberDiscountSessionDates(
        sessionDefinitionToPath(definition),
        getDatesForRestaurantDiscountFetch(definition.restaurantId, datesById),
      );
    }
  }

  const missingDefinitions = getMissingDefinitions(
    unsubsByPath,
    sessionDefs,
    sessionDefinitionToPath,
  );
  const unusedPaths = getUnusedPaths(
    unsubsByPath,
    sessionDefs,
    sessionDefinitionToPath,
  );

  logger.log("Session subscribe", {
    restaurantIds,
    liveSessionPaths: sessionDefs.filter((definition) => !definition.onlyOnce)
      .length,
    onlyOnceSessionPaths: sessionDefs.filter(
      (definition) => definition.onlyOnce,
    ).length,
    missing: missingDefinitions.length,
    unused: unusedPaths.length,
  });

  await Promise.all(unusedPaths.map((path) => unsubPath(unsubsByPath, path)));

  if (abortSignal.aborted) {
    return;
  }

  // onlyOnce defs are always "missing" for getMissingDefinitions; skip Firebase
  // when we already have a cached session snapshot (including from a live path).
  const needsSubscribe = missingDefinitions.filter((definition) => {
    if (!definition.onlyOnce) {
      return true;
    }

    return !latestDiscountSessionSnapshotByPath.has(
      sessionDefinitionToPath(definition),
    );
  });

  await replayDiscountSessionSnapshots(
    sessionDefs,
    needsSubscribe,
    datesById,
    getAvailabilityFromDateChildSnapshot,
    onValue,
  );

  if (abortSignal.aborted) {
    return;
  }

  await Promise.all(
    needsSubscribe.map(({ onlyOnce, ...pathDefinition }) => {
      const path = sessionDefinitionToPath(pathDefinition);
      return subscribeDiscountSession(
        pathDefinition,
        () =>
          onlyOnce
            ? getDatesForRestaurantDiscountFetch(
                pathDefinition.restaurantId,
                datesById,
              )
            : getRememberedDiscountSessionDates(path),
        getAvailabilityFromDateChildSnapshot,
        (unusedPath) => unsubPath(unsubsByPath, unusedPath),
        addUnsubAction,
        onValue,
        abortSignal,
        onlyOnce,
      );
    }),
  );
}

export type FirebaseModel = {
  unsubsByPath: UnsubsByPath;
  addUnsub: Action<FirebaseModel, { path: string; unsub: Unsubscribe }>;
  unsubAll: Action<FirebaseModel>;

  subscribe: Thunk<
    FirebaseModel,
    {
      restaurantIds: number[];
      sessionsById: ByRestaurantId<SessionType[]>;
      datesById: ByRestaurantId<NonEmptyArray<string>>;
      onlyOnceById: ByRestaurantId<boolean>;
      pathMode: FirebaseAvailabilityPathMode;
      onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>;
      abortSignal: AbortSignal;
    }
  >;
};

export const firebaseModel: FirebaseModel = {
  unsubsByPath: {},
  addUnsub: action((state, { path, unsub }) => {
    logger.log("Add unsub:", path);
    addUnsub(state.unsubsByPath, path, unsub);
  }),
  // Sole owner of process-global fingerprint + session snapshot cache clears.
  unsubAll: action((state) => {
    logger.log("Unsub all");
    clearDiscountSessionSnapshotCache();
    unsubAllFromManager(state.unsubsByPath);
    clearAvailabilitySnapshotCache();
  }),

  subscribe: thunk(
    async (
      actions,
      {
        restaurantIds,
        sessionsById,
        datesById,
        onlyOnceById,
        pathMode,
        onValue,
        abortSignal,
      },
      { getState },
    ) => {
      if (abortSignal.aborted) {
        return;
      }

      const unsubsByPath = getState().unsubsByPath;

      if (pathMode === "session") {
        logger.log("Subscribing (session mode)...", {
          restaurantIds,
        });

        await subscribeSessionPaths(
          restaurantIds,
          sessionsById,
          datesById,
          onlyOnceById,
          unsubsByPath,
          (path, unsub) => actions.addUnsub({ path, unsub }),
          onValue,
          abortSignal,
        );

        if (abortSignal.aborted) {
          return;
        }

        logger.log("Subscribed (session mode)", {
          restaurantIds,
        });
        return;
      }

      const dateDefs = getDateSubscribedDefinitions(
        restaurantIds,
        sessionsById,
        datesById,
        onlyOnceById,
      );

      logger.log("Subscribing (date mode)...", {
        restaurantIds,
        liveDatePaths: dateDefs.filter((definition) => !definition.onlyOnce)
          .length,
        onlyOnceDatePaths: dateDefs.filter((definition) => definition.onlyOnce)
          .length,
      });

      await multiSubscribe(
        dateDefs,
        unsubsByPath,
        (path) => unsubPath(unsubsByPath, path),
        (path, unsub) => actions.addUnsub({ path, unsub }),
        dateDefinitionToPath,
        getAvailabilityFromDateChildSnapshot,
        onValue,
        abortSignal,
      );

      if (abortSignal.aborted) {
        return;
      }

      logger.log("Subscribed (date mode)", {
        restaurantIds,
        liveDatePaths: dateDefs.filter((definition) => !definition.onlyOnce)
          .length,
        onlyOnceDatePaths: dateDefs.filter((definition) => definition.onlyOnce)
          .length,
      });
    },
  ),
};
